"use client";
import React, { useState, useEffect } from "react";
import styles from "../css/index.js";
import { searchInventory, getCostPriceBySku } from "../../../../api/dispensary/script/LabelApi";

const TracerLabel = ({ form, forms, pharmacyInfo, totals, drugInfo: propDrugInfo, user, repeatNumber = null, showRepeat = false, repeatData = null }) => {
  const allForms = Array.isArray(forms) && forms.length > 0 ? forms : (form ? [form] : []);
  const firstForm = allForms[0];

  // State for cost prices
  const [costPrices, setCostPrices] = useState({});
  const [totalCost, setTotalCost] = useState(0);

  // Debug: Log all props
  console.log('[TracerLabel] Props received:', { form, forms, pharmacyInfo, totals, drugInfo: propDrugInfo });

  const tracerCodeValue = repeatNumber != null && String(repeatNumber).trim() !== ''
    ? String(repeatNumber).trim()
    : '';

  // Use drug info from props instead of fetching automatically
  const drugInfo = propDrugInfo || {
    strength: null,
    dosageForm: null,
    schedule: null,
    packSize: "",
    costPrice: null
  };

  console.log('TracerLabel drugInfo state:', drugInfo);

  // Function to format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return "0.00";
    }
    return Number(amount).toFixed(2);
  };

  // Fetch cost prices for all medications
  useEffect(() => {
    const fetchCostPrices = async () => {
      const newCostPrices = {};
      let total = 0;

      for (const form of allForms) {
        const medicationName = form?.data?.medication;
        if (medicationName) {
          try {
            // First search inventory to get SKU
            const inventoryResult = await searchInventory(medicationName);
            if (inventoryResult.success && inventoryResult.data && inventoryResult.data.length > 0) {
              const sku = inventoryResult.data[0].sku;
              
              // Then get cost price by SKU
              const costPriceResult = await getCostPriceBySku(sku);
              if (costPriceResult.success && costPriceResult.costPrice !== null) {
                newCostPrices[medicationName] = costPriceResult.costPrice;
                total += parseFloat(costPriceResult.costPrice) || 0;
                console.log(`Cost price for ${medicationName} (SKU: ${sku}): ${costPriceResult.costPrice}`);
              } else {
                newCostPrices[medicationName] = 0;
                console.log(`No cost price found for ${medicationName} (SKU: ${sku})`);
              }
            } else {
              newCostPrices[medicationName] = 0;
              console.log(`No inventory found for ${medicationName}`);
            }
          } catch (error) {
            console.error(`Error fetching cost price for ${medicationName}:`, error);
            newCostPrices[medicationName] = 0;
          }
        }
      }

      setCostPrices(newCostPrices);
      setTotalCost(total);
      console.log('Final cost prices:', newCostPrices);
      console.log('Total cost:', total);
    };

    if (allForms.length > 0) {
      fetchCostPrices();
    }
  }, [allForms]);

  // Clean patient name to remove dependent code and main member ID
  const cleanPatientName = (patientName) => {
    if (!patientName) return '';
    
    let cleanedName = patientName;
    
    // Remove title
    const titlePatterns = [
      /\bMR\s+/i,
      /\bMISS\s+/i,
      /\bMRS\s+/i,
      /\bMS\s+/i,
      /\bDR\s+/i,
      /\bPROF\s+/i
    ];
    
    for (const pattern of titlePatterns) {
      cleanedName = cleanedName.replace(pattern, '');
    }
    
    // Remove codes at the end (pattern like " 01/123456" or "/01/123456")
    cleanedName = cleanedName.replace(/\s*\d+\/\d+$/, '').replace(/\/\d+\/\d+$/, '');
    
    return cleanedName.trim();
  };

  // Function to extract patient title from patient name
  const extractPatientTitle = (patientName) => {
    if (!patientName) return null;
    
    const titlePatterns = [
      /\bMR\b/i,
      /\bMISS\b/i,
      /\bMRS\b/i,
      /\bMS\b/i,
      /\bDR\b/i,
      /\bPROF\b/i
    ];
    
    for (const pattern of titlePatterns) {
      const match = patientName.match(pattern);
      if (match) {
        return match[0].toUpperCase();
      }
    }
    
    return null;
  };

  // Function to extract dependent code and main member ID from patient name
  const extractPatientCodes = (patientName) => {
    if (!patientName) return { dependentCode: '', mainMemberId: '' };
    
    // Look for pattern like "dependentcode/mainmemberid" at the end
    const codeMatch = patientName.match(/(\d+)\/(\d+)$/);
    if (codeMatch) {
      return {
        dependentCode: codeMatch[1],
        mainMemberId: codeMatch[2]
      };
    }
    
    return { dependentCode: '', mainMemberId: '' };
  };

  // Function to format medication line for each form
  const formatMedicationLine = (form, index) => {
    const lineNumber = String(index + 1).padStart(2, '0'); // 01, 02, 03, etc.
    
    // Extract strength from medication name if no drug info available
    let extractedStrength = "";
    if (!drugInfo?.strength && form?.data?.medication) {
      const strengthMatch = form?.data?.medication.match(/\b\d+(?:\.\d+)?\s?(?:mcg|µg|mg|g|ml|mL|IU)\b/i);
      if (strengthMatch) {
        extractedStrength = strengthMatch[0];
      }
    }

    // Extract dosage form from medication name if no drug info available
    let extractedDosageForm = "";
    if (!drugInfo?.dosageForm && form?.data?.medication) {
      const forms = [
        /\btab(?:let)?s?\b/i,
        /\bcap(?:sule)?s?\b/i,
        /\bsuspension\b/i,
        /\bsyrup\b/i,
        /\bcream\b/i,
        /\bointment\b/i,
        /\binjection\b/i,
        /\bdrops\b/i,
        /\bsolution\b/i,
        /\belixir\b/i,
        /\bgel\b/i,
        /\blotion\b/i,
        /\bspray\b/i
      ];
      const formMatch = forms.find(f => f.test(form?.data?.medication));
      if (formMatch) {
        extractedDosageForm = formMatch.exec(form?.data?.medication)?.[0] || "";
      }
    }

    const strengthToShow = drugInfo?.strength || extractedStrength;
    const dosageFormToShow = drugInfo?.dosageForm || extractedDosageForm;
    const packSizeToShow = drugInfo?.packSize || "";

    // Build the medication line: 01: medication name strength dosage form packsize
    let medicationDetails = form?.data?.medication || "";
    if (strengthToShow) medicationDetails += ` ${strengthToShow}`;
    if (dosageFormToShow) medicationDetails += ` ${dosageFormToShow}`;
    if (packSizeToShow) medicationDetails += ` ${packSizeToShow}`;

    return (
      <div className={styles.medicationLineContainer}>
        <span className={styles.medicationLineContent}>
          {lineNumber}: {medicationDetails}
        </span>
        <span className={styles.medicationLineAmount}>
          {formatCurrency(costPrices[form?.data?.medication] || 0)}
        </span>
      </div>
    );
  };

  // Get formatted patient name
  const getFormattedPatientName = () => {
    const patientName = firstForm?.data?.patientName || '';
    const title = extractPatientTitle(patientName);
    const codes = extractPatientCodes(patientName);
    const cleanedName = cleanPatientName(patientName);
    
    // Split cleaned name into parts to reorganize as SURNAME FIRSTNAME
    const nameParts = cleanedName.trim().split(/\s+/);
    let firstname = '';
    let surname = '';
    
    if (nameParts.length === 1) {
      firstname = nameParts[0];
      surname = '';
    } else if (nameParts.length === 2) {
      firstname = nameParts[0];
      surname = nameParts[1];
    } else {
      firstname = nameParts[0];
      surname = nameParts.slice(1).join(' ');
    }
    
    // Build display name: SURNAME FIRSTNAME TITLE
    let displayName = '';
    if (surname) displayName += surname.toUpperCase();
    if (firstname) displayName += (displayName ? ' ' : '') + firstname.toUpperCase();
    if (title) displayName += (displayName ? ' ' : '') + title.toUpperCase();
    
    return displayName || 'PATIENT';
  };

  // Get RX number
  const rxNumber = String(firstForm?.data?.rxNumber || '').trim() || '1416650';

  return (
    <div className={styles.labelCard}>
      <div className={styles.pharmacyHeader}>
        <h3 className={styles.pharmacyName}>{pharmacyInfo.name}</h3>
        <p className={styles.pharmacyDetails}>{pharmacyInfo.address}</p>
        <p className={styles.pharmacyPhone}>{pharmacyInfo.phone}</p>
      </div>

      <div className={styles.prescriptionInfo}>
        {/* Grey divider line between phone and medication */}
        <div className={styles.dividerLine}></div>
        
        {/* First line: RX number, patient name, 6-digit code at end */}
        <div className={styles.tracerFirstLine}>
          <span>Rx: {rxNumber}</span>
          <span className={styles.tracerPatientName}>{getFormattedPatientName()}</span>
          {tracerCodeValue ? (
            <span className={styles.tracerSixDigitCode}>#{tracerCodeValue}</span>
          ) : null}
        </div>

        {/* Medication lines - one for each form */}
        <div className={styles.tracerMedicationSection}>
          {allForms.map((form, index) => (
            <div key={form.id || index} className={styles.tracerMedicationLine}>
              {formatMedicationLine(form, index)}
            </div>
          ))}
        </div>

        {/* Final line: date, dispenser name, +COPY 0.00= 0.00 */}
        <div className={styles.tracerFinalLine}>
          <span>{new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' })}</span>
          <span className={styles.tracerDispenserName}>{user?.surname?.toUpperCase()} {user?.name?.toUpperCase()}</span>
          <span className={styles.tracerCopyText}>+COPY 0.00=</span>
          <div className={styles.tracerFinalAmountColumn}>
            <span className={styles.tracerFinalAmount}>{formatCurrency(totalCost)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TracerLabel;
